import { Component, OnInit } from '@angular/core';

@Component({
  selector: 'app-header',
  imports: [],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  private readonly fontScaleStorageKey = 'accessibility-font-scale';
  private readonly contrastStorageKey = 'accessibility-high-contrast';
  private readonly defaultFontScale = 1;
  private readonly minFontScale = 0.9;
  private readonly maxFontScale = 1.3;
  private readonly fontScaleStep = 0.1;
  isHighContrastEnabled = false;

  ngOnInit(): void {
    this.applySavedFontScale();
    this.applySavedContrastMode();
  }

  skipToContent(event: Event): void {
    event.preventDefault();

    const target = document.querySelector('main') as HTMLElement | null;
    if (!target) {
      return;
    }

    this.focusElement(target);
  }

  skipToFooter(event: Event): void {
    event.preventDefault();

    const footerTarget = document.querySelector('footer, #page-end') as HTMLElement | null;

    if (footerTarget) {
      this.focusElement(footerTarget);
      return;
    }

    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
  }

  increaseFontSize(): void {
    const nextScale = Math.min(this.getCurrentFontScale() + this.fontScaleStep, this.maxFontScale);
    this.applyFontScale(nextScale);
  }

  resetFontSize(): void {
    this.applyFontScale(this.defaultFontScale);
  }

  decreaseFontSize(): void {
    const nextScale = Math.max(this.getCurrentFontScale() - this.fontScaleStep, this.minFontScale);
    this.applyFontScale(nextScale);
  }

  toggleHighContrast(): void {
    this.isHighContrastEnabled = !this.isHighContrastEnabled;
    this.applyContrastMode(this.isHighContrastEnabled);
  }

  private focusElement(element: HTMLElement): void {
    const hadTabIndex = element.hasAttribute('tabindex');

    if (!hadTabIndex) {
      element.setAttribute('tabindex', '-1');
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    element.focus({ preventScroll: true });

    if (!hadTabIndex) {
      element.addEventListener('blur', () => element.removeAttribute('tabindex'), { once: true });
    }
  }

  private applySavedFontScale(): void {
    const savedScale = window.localStorage.getItem(this.fontScaleStorageKey);
    const parsedScale = savedScale ? Number(savedScale) : this.defaultFontScale;
    const safeScale = Number.isFinite(parsedScale) ? parsedScale : this.defaultFontScale;

    this.applyFontScale(safeScale);
  }

  private applyFontScale(scale: number): void {
    const safeScale = Math.min(Math.max(scale, this.minFontScale), this.maxFontScale);

    document.body.style.zoom = `${safeScale}`;
    document.body.style.setProperty('--accessibility-font-scale', `${safeScale}`);
    window.localStorage.setItem(this.fontScaleStorageKey, `${safeScale}`);
  }

  private getCurrentFontScale(): number {
    const savedScale = window.localStorage.getItem(this.fontScaleStorageKey);
    const parsedScale = savedScale ? Number(savedScale) : this.defaultFontScale;

    return Number.isFinite(parsedScale) ? parsedScale : this.defaultFontScale;
  }

  private applySavedContrastMode(): void {
    const savedContrastMode = window.localStorage.getItem(this.contrastStorageKey);
    this.isHighContrastEnabled = savedContrastMode === 'true';
    this.applyContrastMode(this.isHighContrastEnabled);
  }

  private applyContrastMode(enabled: boolean): void {
    document.body.classList.toggle('negative-mode', enabled);
    window.localStorage.setItem(this.contrastStorageKey, String(enabled));
  }
}
